// https://designlab.sgmarkets.com/component-viewer/button
// https://sgithub.fr.world.socgen/pages/sgbootstrap/sg-bootstrap/main/demo/buttons.html

import { isDefined } from '@sgme/fp';
import clsx from 'clsx';
import type { PropsWithChildren, ReactNode, Ref } from 'react';

import type { Color } from '../../../types/colors';
import type { MaterialIcon } from '../../../types/icons';
import type { Size } from '../../../types/layout';

export type SgButtonProps = {
  ref?: Ref<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';

  className?: string;
  style?: React.CSSProperties;
  purpose?: 'primary' | 'outline' | 'discreet' | 'default' | 'flat' | 'link';
  color?: Color | 'none'; // If you pass {undefined} due to the default to 'primary' we can't have btn-{purpose} without color -> always default to btn-purpose-primary
  // 'none' allow us to have btn-{purpose} only
  size?: ButtonSize;
  iconPosition?: IconPosition;
  icon?: MaterialIcon;
  iconOutlined?: boolean;

  fullWidth?: boolean;

  loading?: boolean;
  block?: boolean;
  disabled?: boolean;
  tabIndex?: number;

  progress?: number; // 0 to 100
  progressColor?: Color;

  onClick?: () => void;

  onMouseOver?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseOut?: React.MouseEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;

  'data-e2e'?: string;
};

type ButtonSize = Exclude<Size, 'xxl'>;

type IconPosition = 'start' | 'end' | 'icon-only' | 'none';

export const SgButton = (props: PropsWithChildren<SgButtonProps>) => {
  const {
    ref,
    type = 'button',
    className,
    style,
    purpose = 'primary',
    color = 'primary',
    size = 'md',
    iconPosition,
    icon,
    iconOutlined = false,
    fullWidth = false,
    loading: isLoading = false,
    disabled: isDisabled = false,
    tabIndex,
    block: isBlock = false,
    onClick,
    onMouseOver,
    onMouseOut,
    onFocus,
    onBlur,
    'data-e2e': dataE2e,
    children,
    progress,
    progressColor,
  } = props;

  const buttonStyles = clsx(
    'btn',
    `btn-${size}`,
    purpose === 'default' && 'btn-default',
    purpose === 'primary' && color !== 'none' && `btn-${color}`,
    purpose !== 'primary' && purpose !== 'default' && color !== 'none' && `btn-${purpose}-${color}`,
    purpose !== 'primary' && purpose !== 'default' && color === 'none' && `btn-${purpose}`,
    {
      'btn-icon-start':
        iconPosition === 'start' || ((iconPosition === undefined || iconPosition === 'none') && isLoading),
      'btn-icon-end': iconPosition === 'end',
      'btn-icon': iconPosition === 'icon-only',
      loading: isLoading,
      'btn-block': isBlock,
      'w-100': fullWidth,
      'position-relative': isDefined(progress),
    },
    className,
  );

  return (
    <button
      ref={ref}
      type={type}
      className={buttonStyles}
      style={style}
      onClick={onClick}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={isDisabled}
      data-e2e={dataE2e}
      tabIndex={tabIndex}
    >
      {renderButtonContent(children, isLoading, iconPosition, icon, iconOutlined)}

      {isDefined(progress) && (
        <div
          className={clsx('progress position-absolute bottom-0 start-0 end-0 h-1', {
            'progress-sm': size === 'sm' || size === 'md',
            'progress-md': size === 'lg',
            'progress-lg': size === 'xl',
          })}
        >
          <div className={clsx('progressbar', `bg-${progressColor || 'light'}`)} style={{ width: `${progress}%` }} />
        </div>
      )}
    </button>
  );
};

const renderButtonContent = (
  children: ReactNode,
  isLoading: boolean,
  iconPosition: IconPosition | undefined,
  icon: MaterialIcon | undefined,
  iconOutlined: boolean,
) => {
  if (iconPosition === 'icon-only') {
    return isLoading ? (
      <>
        <span className="loader" />
        <i className="icon">close</i>
      </>
    ) : icon !== undefined ? (
      <i className="icon">{icon}</i>
    ) : (
      children
    );
  }

  if (iconPosition === 'start' || ((iconPosition === undefined || iconPosition === 'none') && isLoading)) {
    return (
      <>
        {isLoading ? (
          <>
            <span className="loader" />
            <i className="icon">close</i>
          </>
        ) : (
          icon !== undefined && (
            <i
              className={clsx('icon', {
                'icon-outlined': iconOutlined,
              })}
            >
              {icon}
            </i>
          )
        )}
        {children}
      </>
    );
  }

  if (iconPosition === 'end') {
    return (
      <>
        {children}
        {isLoading ? (
          <>
            <span className="loader" />
            <i className="icon">close</i>
          </>
        ) : (
          icon !== undefined && <i className="icon">{icon}</i>
        )}
      </>
    );
  }

  return children;
};
